/**
 * TranslationsEditor.jsx
 *
 * SysCLIQ / ssadmin / Licensor only. Grid editor for the i18n registry.
 * Inline edit. "🤖 Auto-suggest (Hindi)" calls Sarvam AI per-row. AR / DE / EL
 * are manual. Press Ctrl+Shift+L to save all dirty rows at once.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { CheckCircle2, AlertTriangle, RefreshCw, Plus, Download, Sparkles, Trash2, Save, Languages } from 'lucide-react';
import { API, authFetch } from '../lib/api';
import { useI18n } from '../i18n/I18nContext.jsx';
import { NAV_ITEMS } from '../shell/NAV_ITEMS';

// Fallback if /api/i18n/locales is not reachable; matches backend catalogue.
const FALLBACK_LOCALE_HEADERS = [
  { code: 'en-IN', label: 'EN (source)', readonly: true,  provider: 'source' },
  { code: 'hi-IN', label: 'हिन्दी',         provider: 'sarvam' },
  { code: 'ta-IN', label: 'தமிழ்',          provider: 'sarvam' },
  { code: 'te-IN', label: 'తెలుగు',         provider: 'sarvam' },
  { code: 'mr-IN', label: 'मराठी',         provider: 'sarvam' },
  { code: 'bn-IN', label: 'বাংলা',         provider: 'sarvam' },
  { code: 'gu-IN', label: 'ગુજરાતી',        provider: 'sarvam' },
  { code: 'kn-IN', label: 'ಕನ್ನಡ',         provider: 'sarvam' },
  { code: 'ml-IN', label: 'മലയാളം',      provider: 'sarvam' },
  { code: 'pa-IN', label: 'ਪੰਜਾਬੀ',          provider: 'sarvam' },
  { code: 'or-IN', label: 'ଓଡ଼ିଆ',          provider: 'sarvam' },
  { code: 'ur-IN', label: 'اردو',          provider: 'sarvam' },
  { code: 'ar-AE', label: 'العربية',        provider: 'gemini' },
  { code: 'de-DE', label: 'Deutsch',      provider: 'gemini' },
  { code: 'fr-FR', label: 'Français',     provider: 'gemini' },
  { code: 'it-IT', label: 'Italiano',     provider: 'gemini' },
  { code: 'es-ES', label: 'Español',      provider: 'gemini' },
  { code: 'el-GR', label: 'Ελληνικά',      provider: 'gemini' },
];

const PROVIDER_COLOR = {
  sarvam: { fg: '#8b5cf6', bg: 'rgba(139,92,246,0.1)' },   // purple
  gemini: { fg: '#0ea5e9', bg: 'rgba(14,165,233,0.1)'  },   // sky blue
  source: { fg: '#64748b', bg: 'transparent' },
};

export default function TranslationsEditor() {
  const { refresh: refreshActiveDict, locales: liveLocales } = useI18n();
  // Build LOCALE_HEADERS dynamically from the live server catalogue so the
  // editor never lags behind backend changes (new languages auto-appear).
  const LOCALE_HEADERS = useMemo(() => {
    if (!liveLocales || liveLocales.length === 0) return FALLBACK_LOCALE_HEADERS;
    return liveLocales.map(l => ({
      code:     l.code,
      label:    l.code === 'en-IN' ? 'EN (source)' : l.label,
      provider: l.provider || (l.code === 'en-IN' ? 'source' : (l.code.endsWith('-IN') ? 'sarvam' : 'gemini')),
      readonly: l.code === 'en-IN',
    }));
  }, [liveLocales]);

  const [rows,       setRows]       = useState([]);
  const [scopeFilter, setScopeFilter] = useState('');
  const [search,     setSearch]     = useState('');
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [dirty,      setDirty]      = useState({});   // key: row#_lang  → value
  const [savingAll,  setSavingAll]  = useState(false);
  const [toast,      setToast]      = useState(null);
  const [showAdd,    setShowAdd]    = useState(false);
  const [scopes,     setScopes]     = useState([]);
  // Bulk-fill-one-language progress: { lang, done, total, running }
  const [fillProgress, setFillProgress] = useState(null);
  const fillCancelRef = React.useRef(false);

  const RTL_CODES = useMemo(() => new Set(['ar-AE', 'ur-IN', 'fa-IR', 'he-IL']), []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = new URL(`${API}/i18n/admin/matrix`);
      if (scopeFilter) url.searchParams.set('scope', scopeFilter);
      if (search)      url.searchParams.set('q', search);
      const r = await authFetch(url.toString());
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setRows(d.rows || []);
      setDirty({});
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [scopeFilter, search]);

  const fetchScopes = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/i18n/admin/scopes`);
      const d = await r.json();
      setScopes(d.scopes || []);
    } catch (e) { console.warn('[TranslationsEditor] Scopes fetch failed:', e.message); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchScopes(); }, [fetchScopes]);

  const dirtyCount = useMemo(() => Object.keys(dirty).length, [dirty]);

  const saveAll = useCallback(async () => {
    if (!dirtyCount) return;
    setSavingAll(true);
    setToast(null);
    try {
      const rowsToSave = Object.entries(dirty).map(([k, value]) => {
        const [scope, key, lang] = k.split('\u0001');
        return { scope, key, lang, value };
      });
      const r = await authFetch(`${API}/i18n/admin/bulk`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ rows: rowsToSave }),
      });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.message || `HTTP ${r.status}`);
      setToast({ type: 'ok', msg: `${d.written} entries saved.` });
      setDirty({});
      await fetchAll();
      refreshActiveDict();   // also refresh the live i18n so admin sees their own edits
    } catch (e) {
      setToast({ type: 'err', msg: 'Save failed: ' + e.message });
    } finally {
      setSavingAll(false);
    }
  }, [dirty, dirtyCount, fetchAll, refreshActiveDict]);

  // Ctrl+Shift+L save shortcut
  useEffect(() => {
    const onKey = (e) => {
      // Cross-platform: Ctrl on Windows/Linux, Meta(Cmd) on macOS — both supported.
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'L' || e.key === 'l')) {
        e.preventDefault();
        saveAll();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [saveAll]);

  const onEdit = (scope, key, lang, value) => {
    const dk = `${scope}\u0001${key}\u0001${lang}`;
    setDirty(prev => ({ ...prev, [dk]: value }));
  };

  const suggest = async (row, targetLang) => {
    const sourceText = row.values['en-IN'];
    if (!sourceText) { setToast({ type: 'err', msg: 'Source (EN) is empty — fill it first.' }); return; }
    try {
      const r = await authFetch(`${API}/i18n/admin/auto-suggest`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: sourceText, sourceLang: 'en-IN', targetLang }),
      });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.message || `HTTP ${r.status}`);
      onEdit(row.scope, row.key, targetLang, d.translated);
      setToast({ type: 'ok', msg: `✨ ${d.provider || 'auto'} suggestion applied for ${targetLang}. Press Ctrl+Shift+L to save.` });
    } catch (e) { setToast({ type: 'err', msg: `Auto-suggest (${targetLang}) failed: ${e.message}` }); }
  };

  // Fill every missing cell for a single language column at once.
  // Auto-saves each translation as it arrives (admin doesn't have to press save).
  const fillEntireLanguage = useCallback(async (targetLang, displayLabel) => {
    const targets = rows.filter(r => {
      const dk = `${r.scope}\u0001${r.key}\u0001${targetLang}`;
      const live = dirty[dk] != null ? dirty[dk] : (r.values[targetLang] || '');
      return r.values['en-IN'] && !live.trim();
    });
    if (targets.length === 0) {
      setToast({ type: 'ok', msg: `${displayLabel} is already complete — nothing to translate.` });
      return;
    }
    if (!window.confirm(
      `Auto-translate ${targets.length} missing entries to ${displayLabel}?\n\n` +
      `Each row is translated + saved immediately. May take ~${Math.ceil(targets.length * 0.5)}s.\n` +
      `You can cancel mid-way.`
    )) return;

    fillCancelRef.current = false;
    setFillProgress({ lang: targetLang, done: 0, total: targets.length, running: true, errors: 0 });
    let done = 0, errors = 0;
    for (const row of targets) {
      if (fillCancelRef.current) break;
      try {
        const r = await authFetch(`${API}/i18n/admin/auto-suggest`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: row.values['en-IN'], sourceLang: 'en-IN', targetLang }),
        });
        const d = await r.json();
        if (!r.ok || !d.ok) throw new Error(d.message || `HTTP ${r.status}`);
        // Persist immediately so progress is durable even if browser closes
        await authFetch(`${API}/i18n/admin/upsert`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ scope: row.scope, key: row.key, lang: targetLang, value: d.translated, source: d.provider }),
        });
        // Reflect in the editor immediately (local row mutation)
        row.values[targetLang] = d.translated;
        done++;
      } catch (e) {
        errors++;
        // Hard-stop on quota — pointless to keep hammering
        if (/quota|429/i.test(e.message)) {
          setToast({ type: 'err', msg: `${displayLabel}: quota exhausted at row ${done + 1}/${targets.length}. Try again after a minute.` });
          break;
        }
      }
      setFillProgress(p => p ? { ...p, done: done, errors } : null);
      await new Promise(r => setTimeout(r, 350));   // friendly RPS
    }
    setFillProgress(null);
    fillCancelRef.current = false;
    await fetchAll();
    refreshActiveDict();
    setToast({
      type: errors > 0 ? 'err' : 'ok',
      msg: `${displayLabel}: filled ${done}/${targets.length}${errors > 0 ? `, ${errors} failed` : ''}.`,
    });
  }, [rows, dirty, fetchAll, refreshActiveDict]);

  const cancelFill = () => { fillCancelRef.current = true; };

  const downloadBundleZip = async () => {
    try {
      const res = await authFetch(`${API}/i18n/bundle/download`);
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`HTTP ${res.status}: ${t.slice(0, 160)}`);
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = 'msn-cliq-i18n-bundle.zip';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { setToast({ type: 'err', msg: 'Bundle download failed: ' + e.message }); }
  };

  const [syncProgress, setSyncProgress] = useState(null); // null | { running, done, total, synced, skipped, failed }

  const syncScreenLabels = useCallback(async () => {
    // Build nav label payload: scope='nav', key=suffix only (t() looks up flat 'nav.dashboard')
    const navLabels  = NAV_ITEMS.map(item => ({
      scope: 'nav',
      key: item.key.replace(/^nav\./, ''),  // 'nav.dashboard' → 'dashboard'
      en: item.label,
    }));

    // Also fetch UAMS tree labels from backend
    let uamsLabels = [];
    try {
      const treeResp = await authFetch(`${API}/uams/tree`);
      const treeData = treeResp.ok ? await treeResp.json() : {};
      const tree = treeData.tree || [];
      for (const mod of tree) {
        if (mod.ModuleName) uamsLabels.push({ scope: 'uams', key: `m.${mod.ModuleId}`, en: mod.ModuleName });
        for (const sub of (mod.subModules || [])) {
          if (sub.SubModuleName) uamsLabels.push({ scope: 'uams', key: `s.${sub.SubModuleId}`, en: sub.SubModuleName });
          for (const scr of (sub.screens || [])) {
            if (scr.MenuTitle) uamsLabels.push({ scope: 'uams', key: `scr.${scr.SeqId}`, en: scr.MenuTitle });
          }
        }
      }
      // Deduplicate by scope+key
      const seen = new Set();
      uamsLabels = uamsLabels.filter(l => { const id = `${l.scope}|${l.key}`; if (seen.has(id)) return false; seen.add(id); return true; });
    } catch (e) { console.warn('[i18n] Could not fetch UAMS tree:', e.message); }

    const labels = [...navLabels, ...uamsLabels];
    if (!window.confirm(
      `Sync ${labels.length} screen labels (${navLabels.length} nav + ${uamsLabels.length} UAMS)\n` +
      `to all Sarvam Indic languages?\n\nThis may take 1-2 minutes. Skips already-translated entries.`
    )) return;

    setSyncProgress({ running: true, total: labels.length, synced: 0, skipped: 0, failed: 0 });
    setToast(null);
    try {
      const r = await authFetch(`${API}/i18n/admin/sync-labels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ labels, force: false }),
      });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.message || `HTTP ${r.status}`);
      setSyncProgress({ running: false, ...d });
      setToast({ type: 'ok', msg: `✓ Synced ${d.synced} translations (${d.skipped} skipped, ${d.failed} failed)` });
      await fetchAll();
      refreshActiveDict();
    } catch (e) {
      setSyncProgress(null);
      setToast({ type: 'err', msg: 'Sync failed: ' + e.message });
    }
  }, [fetchAll, refreshActiveDict]);

  const deleteRow = async (row) => {
    if (!window.confirm(`Delete caption ${row.scope}.${row.key} (all locales)?`)) return;
    try {
      const r = await authFetch(`${API}/i18n/admin/key?scope=${encodeURIComponent(row.scope)}&key=${encodeURIComponent(row.key)}`, { method: 'DELETE' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || `HTTP ${r.status}`);
      setToast({ type: 'ok', msg: `Deleted ${d.deleted} row(s).` });
      await fetchAll();
    } catch (e) { setToast({ type: 'err', msg: 'Delete failed: ' + e.message }); }
  };

  return (
    <div className="card" style={{ borderTop: '3px solid #8b5cf6' }} data-testid="translations-editor">
      <div className="card-header">
        <div>
          <div className="card-title">Translations Registry</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
            18 locales · Click <strong style={{ color: '#0ea5e9' }}>✨ Fill N</strong> in any column header to auto-translate just that language.
            Per-row ✨ button for single-cell suggestions. <strong style={{ marginLeft: 6 }}>Ctrl+Shift+L</strong> saves edits.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            onClick={syncScreenLabels}
            className="btn btn-outline"
            style={{ fontSize: 10, borderColor: '#8b5cf6', color: '#8b5cf6' }}
            data-testid="i18n-sync-screen-labels"
            title="Auto-translate all nav + UAMS screen labels using Sarvam AI"
            disabled={syncProgress?.running}
          >
            {syncProgress?.running
              ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Syncing…</>
              : <><Languages size={12} /> Sync Screen Labels</>}
          </button>
          <button
            type="button"
            onClick={downloadBundleZip}
            className="btn btn-outline"
            style={{ fontSize: 10 }}
            data-testid="i18n-download-bundle"
            title="Download standalone i18n bundle (drop-in for any project)"
          >
            <Download size={12} /> Download Bundle ZIP
          </button>
          <button className="btn btn-outline" style={{ fontSize: 10 }} onClick={() => setShowAdd(true)} data-testid="i18n-add-caption">
            <Plus size={12} /> New Caption
          </button>
          <button
            className="btn btn-primary"
            style={{ fontSize: 10 }}
            onClick={saveAll}
            disabled={savingAll || !dirtyCount}
            data-testid="i18n-save-all"
            title="Ctrl+Shift+L"
          >
            <Save size={12} /> Save {dirtyCount > 0 ? `(${dirtyCount})` : ''}
          </button>
        </div>
      </div>

      <div style={{ padding: '12px 16px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', borderBottom: '1px solid var(--border, #e2e8f0)' }}>
        <select className="form-select" value={scopeFilter} onChange={e => setScopeFilter(e.target.value)} style={{ minWidth: 180 }} data-testid="i18n-scope-filter">
          <option value="">All scopes ({scopes.length})</option>
          {scopes.map(s => <option key={s.scope} value={s.scope}>{s.scope} ({s.keyCount})</option>)}
        </select>
        <input
          className="form-input"
          placeholder="Search key or value…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && fetchAll()}
          style={{ flex: 1, minWidth: 180 }}
          data-testid="i18n-search"
        />
        <button className="btn btn-outline" onClick={fetchAll} disabled={loading} style={{ fontSize: 10 }} data-testid="i18n-refresh">
          <RefreshCw size={12} className={loading ? 'spin' : ''} /> Refresh
        </button>
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{rows.length} rows</span>
      </div>

      {toast && (
        <div className={`alert-banner ${toast.type === 'ok' ? 'green' : 'red'}`} style={{ margin: 12 }} data-testid="i18n-toast">
          {toast.type === 'ok' ? <CheckCircle2 size={14} color="#16a34a" /> : <AlertTriangle size={14} color="#dc2626" />}
          <div style={{ fontSize: 12 }}>{toast.msg}</div>
        </div>
      )}
      {error && (
        <div className="alert-banner red" style={{ margin: 12 }}>
          <AlertTriangle size={14} color="#dc2626" />
          <div style={{ fontSize: 12 }}>{error}</div>
        </div>
      )}

      {fillProgress && fillProgress.running && (
        <div
          className="alert-banner"
          style={{
            margin: 12, background: 'rgba(14,165,233,0.08)', border: '1px solid #0ea5e9',
            display: 'flex', alignItems: 'center', gap: 12,
          }}
          data-testid="i18n-fill-progress"
        >
          <Sparkles size={14} color="#0ea5e9" />
          <div style={{ fontSize: 12, flex: 1 }}>
            Filling <strong>{fillProgress.lang}</strong> — {fillProgress.done}/{fillProgress.total} done
            {fillProgress.errors > 0 && <span style={{ color: '#dc2626', marginLeft: 6 }}> · {fillProgress.errors} failed</span>}
          </div>
          <div style={{ width: 200, height: 6, background: 'rgba(148,163,184,0.2)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              width: `${(fillProgress.done / fillProgress.total) * 100}%`,
              height: '100%', background: '#0ea5e9', transition: 'width 0.3s'
            }} />
          </div>
          <button className="btn btn-outline" style={{ fontSize: 10, color: '#dc2626' }} onClick={cancelFill} data-testid="i18n-fill-cancel">
            Cancel
          </button>
        </div>
      )}

      <div
        className="translations-editor-wrap"
        style={{
          padding: 12,
          overflow: 'auto',
          maxHeight: '70vh',          // viewport-sized scroll area; header + first 3 cols freeze
          position: 'relative',
        }}
        data-testid="i18n-table-scroll"
      >
        <style>{`
          .i18n-grid             { border-collapse: separate; border-spacing: 0; min-width: 1200px; width: 100%; font-size: 12px; }
          .i18n-grid thead th    { position: sticky; top: 0; z-index: 3; background: var(--surface-1, #ffffff); box-shadow: 0 1px 0 var(--border, #e2e8f0); padding: 8px; text-align: left; }
          .i18n-grid td          { padding: 4px; vertical-align: top; border-bottom: 1px solid var(--border, #f1f5f9); background: var(--surface-1, #ffffff); }
          /* Three frozen columns: Scope (0px), Key (110px), EN-source (270px) */
          .i18n-grid .sticky-col-0 { position: sticky; left: 0;    z-index: 2; box-shadow: 1px 0 0 var(--border, #e2e8f0); min-width: 110px; max-width: 110px; }
          .i18n-grid .sticky-col-1 { position: sticky; left: 110px; z-index: 2; box-shadow: 1px 0 0 var(--border, #e2e8f0); min-width: 160px; max-width: 160px; }
          .i18n-grid .sticky-col-2 { position: sticky; left: 270px; z-index: 2; box-shadow: 2px 0 0 var(--border, #cbd5e1); min-width: 220px; max-width: 220px; }
          .i18n-grid thead .sticky-col-0,
          .i18n-grid thead .sticky-col-1,
          .i18n-grid thead .sticky-col-2 { z-index: 4; }
          .i18n-grid tr:hover td { background: var(--surface-2, #f8fafc); }
        `}</style>

        <table className="i18n-grid">
          <thead>
            <tr>
              <th className="sticky-col-0">Scope</th>
              <th className="sticky-col-1">Key</th>
              {LOCALE_HEADERS.map((h, idx) => {
                const headerCls = idx === 0 ? 'sticky-col-2' : '';
                const colour = PROVIDER_COLOR[h.provider] || PROVIDER_COLOR.source;
                const missing = rows.filter(r => {
                  const dk = `${r.scope}\u0001${r.key}\u0001${h.code}`;
                  const live = dirty[dk] != null ? dirty[dk] : (r.values[h.code] || '');
                  return !live.trim();
                }).length;
                return (
                  <th key={h.code} className={headerCls} style={{ minWidth: 220 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span>{h.label}</span>
                        <span style={{ fontSize: 9, fontWeight: 400, color: colour.fg }}>
                          {h.provider} · {missing > 0 ? `${missing} missing` : '✓ complete'}
                        </span>
                      </div>
                      {h.code !== 'en-IN' && missing > 0 && (
                        <button
                          title={`Auto-translate ${missing} missing rows to ${h.label}`}
                          onClick={() => fillEntireLanguage(h.code, h.label)}
                          disabled={fillProgress && fillProgress.running}
                          data-testid={`i18n-fill-${h.code}`}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '3px 6px', fontSize: 9, fontWeight: 700,
                            background: colour.bg, border: `1px solid ${colour.fg}`,
                            color: colour.fg, borderRadius: 4, cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <Sparkles size={10} /> Fill {missing}
                        </button>
                      )}
                    </div>
                  </th>
                );
              })}
              <th style={{ width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={`${row.scope}.${row.key}`} data-testid={`i18n-row-${i}`}>
                <td className="sticky-col-0" style={{ color: 'var(--text-muted)', fontFamily: 'monospace' }}>{row.scope}</td>
                <td className="sticky-col-1" style={{ fontWeight: 600, fontFamily: 'monospace' }}>
                  <div>{row.key}</div>
                  {(row.control_id || row.screen_name || row.project_name) && (
                    <div style={{ fontSize: 9, fontWeight: 400, color: '#0ea5e9', marginTop: 2, lineHeight: 1.4 }} title="Address registered for this caption">
                      {row.control_id  && <div>🎯 <code>{row.control_id}</code></div>}
                      {row.screen_name && <div style={{ color: 'var(--text-muted)' }}>📺 {row.screen_name}</div>}
                      {row.project_name && <div style={{ color: 'var(--text-muted)' }}>📦 {row.project_name}</div>}
                    </div>
                  )}
                </td>
                {LOCALE_HEADERS.map((h, hIdx) => {
                  const dk = `${row.scope}\u0001${row.key}\u0001${h.code}`;
                  const live = dirty[dk] != null ? dirty[dk] : (row.values[h.code] || '');
                  const isDirty = dirty[dk] != null;
                  const isRTL = RTL_CODES.has(h.code);
                  const cellCls = hIdx === 0 ? 'sticky-col-2' : '';
                  return (
                    <td key={h.code} className={cellCls}>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
                        <textarea
                          value={live}
                          onChange={e => onEdit(row.scope, row.key, h.code, e.target.value)}
                          rows={Math.max(1, Math.min(3, Math.ceil(live.length / 30)))}
                          dir={isRTL ? 'rtl' : 'ltr'}
                          data-testid={`i18n-cell-${i}-${h.code}`}
                          style={{
                            width: '100%', minHeight: 26, padding: '4px 6px',
                            fontSize: 11, fontFamily: isRTL ? 'system-ui' : 'inherit',
                            border: `1px solid ${isDirty ? '#f59e0b' : 'var(--border, #e2e8f0)'}`,
                            background: isDirty ? '#fffbeb' : 'var(--surface, #ffffff)',
                            borderRadius: 4, resize: 'vertical', outline: 'none',
                            color: isDirty ? '#92400e' : 'var(--text-primary, #0f172a)',
                          }}
                          placeholder={h.code === 'en-IN' ? 'source text…' : '—'}
                        />
                        {h.provider && h.provider !== 'source' && (
                          <button
                            title={`Auto-suggest this row via ${h.provider}`}
                            onClick={() => suggest(row, h.code)}
                            data-testid={`i18n-suggest-${i}-${h.code}`}
                            style={{
                              padding: 3,
                              background: PROVIDER_COLOR[h.provider].bg,
                              border: `1px solid ${PROVIDER_COLOR[h.provider].fg}`,
                              borderRadius: 4,
                              color: PROVIDER_COLOR[h.provider].fg,
                              cursor: 'pointer', flexShrink: 0,
                            }}
                          >
                            <Sparkles size={10} />
                          </button>
                        )}
                      </div>
                    </td>
                  );
                })}
                <td>
                  <button onClick={() => deleteRow(row)} className="btn btn-outline" style={{ fontSize: 10, color: '#dc2626' }} data-testid={`i18n-delete-${i}`}>
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={LOCALE_HEADERS.length + 3} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No translations yet. Click <strong>New Caption</strong> to add one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showAdd && <AddCaptionModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); fetchAll(); fetchScopes(); }} />}
    </div>
  );
}

function AddCaptionModal({ onClose, onSaved }) {
  const [scope, setScope] = useState('');
  const [key,   setKey]   = useState('');
  const [value, setValue] = useState('');
  const [projectName, setProjectName] = useState('');
  const [screenName,  setScreenName]  = useState('');
  const [controlId,   setControlId]   = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr(null);
    try {
      const r = await authFetch(`${API}/i18n/admin/upsert`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scope: scope.trim(), key: key.trim(), lang: 'en-IN', value,
          project_name: projectName.trim() || null,
          screen_name:  screenName.trim()  || null,
          control_id:   controlId.trim()   || null,
        }),
      });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.message || `HTTP ${r.status}`);
      onSaved();
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  return (
    <div data-testid="i18n-add-modal" style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
    }}>
      <form onSubmit={submit} className="card" style={{ maxWidth: 560, width: '90%', padding: 24, background: 'var(--surface-1, #fff)', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ margin: 0, marginBottom: 14, fontSize: 16 }}>Add new caption (EN source)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label className="form-label">Scope *</label>
            <input className="form-input" value={scope} onChange={e => setScope(e.target.value)} placeholder="signup.form" required data-testid="i18n-add-scope" />
          </div>
          <div>
            <label className="form-label">Key *</label>
            <input className="form-input" value={key} onChange={e => setKey(e.target.value)} placeholder="submit_button" required data-testid="i18n-add-key" />
          </div>
        </div>
        <label className="form-label" style={{ marginTop: 12 }}>English text *</label>
        <textarea className="form-input" rows={3} value={value} onChange={e => setValue(e.target.value)} required data-testid="i18n-add-value" />

        <div style={{ marginTop: 18, padding: 12, background: 'rgba(14,165,233,0.05)', border: '1px solid rgba(14,165,233,0.25)', borderRadius: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#0ea5e9', marginBottom: 8 }}>Control Address (optional but recommended)</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 10 }}>
            Pin this caption to a specific UI control so it can be looked up by <code>control_id</code> from any project.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div>
              <label className="form-label">Project</label>
              <input className="form-input" value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="RetailCLIQ_ERP" data-testid="i18n-add-project" />
            </div>
            <div>
              <label className="form-label">Screen</label>
              <input className="form-input" value={screenName} onChange={e => setScreenName(e.target.value)} placeholder="PublicSignup" data-testid="i18n-add-screen" />
            </div>
            <div>
              <label className="form-label">Control ID</label>
              <input className="form-input" value={controlId} onChange={e => setControlId(e.target.value)} placeholder="btnSignupSubmit" data-testid="i18n-add-control" />
            </div>
          </div>
        </div>

        {err && <div style={{ color: '#dc2626', fontSize: 11, marginTop: 8 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving} data-testid="i18n-add-submit">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
}
